import React, { useEffect, useState } from "react";

import Icon from "../Icon";
import Chain from "./Chain";
import Group from "./Group";
import PlaceholderGroup from "./PlaceholderGroup";
import "./styles.scss";

const NARROW_VIEWPORT = '(max-width: 1200px)';

const readCollapsed = key => {
    try {
        return localStorage.getItem(key) === 'true';
    } catch (e) {
        return false;
    }
};

const writeCollapsed = (key, value) => {
    try {
        localStorage.setItem(key, value ? 'true' : 'false');
    } catch (e) {
        // Navegador com storage bloqueado: a coluna só perde a memória do
        // estado, continua funcionando.
    }
};

/**
 * Coluna lateral de vínculos (BDM-1051): o que existe em volta do registro
 * aberto, agrupado por tipo. Não sabe o que é pedido — recebe o payload de
 * `{chain, groups}` e um `config` por grupo (ícone, "ver todos" e ação do
 * estado vazio), então serve orçamento e pedido de compra do mesmo jeito.
 */
export const LinkedRecords = ({
    title = 'Vinculados',
    chainTitle,
    loading = false,
    chain = [],
    chainLabels = {},
    groups = [],
    config = {},
    itemTo,
    storageKey = 'linked-records.collapsed',
    placeholders = [],
    placeholdersBefore,
}) => {
    const [collapsed, setCollapsed] = useState(() => readCollapsed(storageKey));

    // Em tela estreita a coluna recolhe sozinha, sem gravar: a preferência do
    // usuário volta assim que a janela cresce de novo.
    useEffect(() => {
        if (!window.matchMedia) return;

        const query = window.matchMedia(NARROW_VIEWPORT);
        const apply = narrow => setCollapsed(narrow ? true : readCollapsed(storageKey));

        apply(query.matches);

        const handler = event => apply(event.matches);
        query.addEventListener('change', handler);

        return () => query.removeEventListener('change', handler);
    }, [storageKey]);

    const toggle = () => {
        const next = !collapsed;
        setCollapsed(next);
        writeCollapsed(storageKey, next);
    };

    const total = groups.reduce((sum, group) => sum + group.count, 0);

    // Grupos que o plano ainda não tem entram na posição em que apareceriam
    // (antes de `placeholdersBefore`), em vez de empilhados no fim.
    const insertAt = groups.findIndex(group => group.key === placeholdersBefore);
    const rows = [...groups.map(group => ({ group }))];
    rows.splice(insertAt === -1 ? rows.length : insertAt, 0, ...placeholders.map(placeholder => ({ placeholder })));

    if (collapsed) {
        return (
            <aside className="linked-dock" aria-label={`${title} (recolhido)`}>
                <button type="button" onClick={toggle} aria-label={`Abrir ${title.toLowerCase()}`}>
                    <Icon name="arrow" size={16} className="flip" />
                </button>
                <hr />
                {rows.map(({ group, placeholder }) => group ? (
                    <button
                        type="button"
                        key={group.key}
                        onClick={toggle}
                        title={`${group.label}: ${group.count}`}
                        aria-label={`${group.label}: ${group.count}`}
                    >
                        <Icon name={config[group.key]?.icon ?? 'link'} size={16} />
                        {group.count > 0 && (
                            <span className={`linked-dot ${group.alert > 0 ? 'alert' : ''}`}>{group.count}</span>
                        )}
                    </button>
                ) : (
                    <button
                        type="button"
                        key={placeholder.key}
                        className="is-placeholder"
                        onClick={placeholder.onClick ?? toggle}
                        title={`${placeholder.label}: ${placeholder.hint}`}
                        aria-label={`${placeholder.label}: ${placeholder.hint}`}
                    >
                        <Icon name={config[placeholder.key]?.icon ?? 'link'} size={16} />
                    </button>
                ))}
            </aside>
        );
    }

    return (
        <aside className="linked-records" aria-label={title}>
            <div className="linked-head">
                <h3>
                    {title}
                    {!loading && <span className="linked-count">{total}</span>}
                </h3>
                <button type="button" onClick={toggle} aria-label={`Recolher ${title.toLowerCase()}`}>
                    <Icon name="arrow" size={16} />
                </button>
            </div>

            <div className="linked-scroll">
                {loading ? (
                    <div className="linked-skeleton" aria-hidden="true">
                        {[0, 1, 2, 3, 4].map(row => <span key={row} />)}
                    </div>
                ) : (
                    <>
                        <Chain steps={chain} labels={chainLabels} title={chainTitle} />
                        {rows.map(({ group, placeholder }) => group ? (
                            <Group
                                key={group.key}
                                group={group}
                                config={config[group.key] ?? {}}
                                itemTo={itemTo}
                            />
                        ) : (
                            <PlaceholderGroup
                                key={placeholder.key}
                                placeholder={placeholder}
                                icon={config[placeholder.key]?.icon}
                            />
                        ))}
                    </>
                )}
            </div>
        </aside>
    );
}

export default LinkedRecords;
